import { request, type PlanIngestionSessionOut, type ModelOptionOut, type ModelCallOut } from '../api';
import type { Project, RecycleBinEntry } from '../types';

export type DraftPlan = {
  session_id: string;
  status: string;
  current_step: string;
  requirement: string;
  models: Record<string, string>;
  project_outline: {
    name: string;
    target_users: string;
    positioning: string;
    overall_goal: string;
    business_context: string;
    scope: string[];
    out_of_scope: string[];
    risks: string[];
    project_acceptance_goal: string[];
  };
  stages: Array<{
    id: string;
    order: number;
    name: string;
    goal: string;
    module_scope: string;
    deliverables: string[];
    risks: string[];
    dependencies: string[];
    stage_acceptance: string[];
    stage_execution_route: Record<string, unknown>;
    progress_rule: Record<string, unknown>;
  }>;
  tasks: Array<{
    id: string;
    stage_id: string;
    order: number;
    title: string;
    type: string;
    objective: string;
    dependencies: string[];
    expected_deliverable: string;
    task_acceptance: string[];
    difficulty: string;
    effort: string;
    risk_level: string;
    needs_terminal: boolean;
    needs_build_or_test: boolean;
    status: string;
    execution_route: Record<string, unknown>;
  }>;
  project_acceptance: string[];
  project_progress_model: Record<string, unknown>;
  formal_object_state: {
    created_project_id: string | null;
    created_stage_plan_id: string | null;
    created_task_package_count: number;
    created_run_count: number;
  };
  outline_quality_gate?: {
    passed: boolean;
    reasons: string[];
  };
  technical_details?: {
    outline_model?: string;
    outline_call_status?: string;
    outline_parse_status?: string;
    failure_summary?: string;
  };
};

export type DraftSummary = {
  session_id: string;
  name: string;
  current_step: string;
  outline_confirmed: boolean;
  created_project_id: string | null;
  created_stage_plan_id: string | null;
  updated_at: string | null;
  model: string;
};

export type ProjectDashboard = {
  project_id: string;
  project_name: string;
  status: string;
  user_status?: string;
  project_user_view?: {
    project_status?: string;
    user_visible_project_status?: string;
    needs_user_action?: boolean;
    [key: string]: unknown;
  };
  planning_progress: Record<string, boolean>;
  engineering_progress: Record<string, number>;
  accepted_delivery_progress: Record<string, number>;
  current_stage: any;
  current_task: any;
  current_blocker: string;
  waiting_for: string;
  next_action: string;
  stages: any[];
  final_acceptance: any;
  repair_plan?: any;
  delivery_artifacts?: Array<{
    task_id: string;
    title: string;
    stage_id: string;
    stage_name: string;
    preview_url: string;
    download_url: string;
  }>;
  delivery_center?: any;
  // Phase 2 main chain: dashboard renders 「开始执行第一个任务」 only
  // when this is set AND its stage_id is the first non-completed stage
  // AND task_id is null (no Task row yet).
  first_task_package?: null | {
    stage_id: string;
    stage_name: string;
    task_package_id: string;
    title: string;
    task_id?: string | null;
  };
  session_id?: string | null;
};

// Phase 2 main chain: persistent task-draft types
export type TaskDraft = {
  id: string;
  session_id: string;
  blueprint_phase_id: string;
  source_stage_id: string;
  source_stage_ids: string[];
  title: string;
  objective: string;
  expected_deliverable: string;
  task_acceptance: string[];
  difficulty: string;
  effort: string;
  risk_level: string;
  priority: string;
  recommended_code_model: string | null;
  recommended_route_reason: string;
  executor_agent: string;
  backup_route: string;
  allow_auto_upgrade: boolean;
  dependency_task_draft_ids: string[];
  status: string;
  sort_order: number;
  created_at: string | null;
  updated_at: string | null;
};

export type TaskDraftsResponse = {
  session_id: string;
  task_plan_status: 'not_generated' | 'draft_ready' | 'user_confirmed';
  task_plan_revision: number;
  drafts: TaskDraft[];
  quality_gate?: TaskPlanQualityGate;
  generation_context?: Record<string, unknown>;
};

export type TaskPlanQualityGate = {
  passed: boolean;
  status_text: string;
  issues: {
    category: string;
    code?: string;
    problem_type?: string;
    draft_id: string | null;
    task_id?: string | null;
    task_title?: string | null;
    requirement_text?: string | null;
    source_clause?: string | null;
    source_requirement?: string | null;
    missing_field?: string | null;
    field_path?: string | null;
    current_value?: unknown;
    expected_value?: unknown;
    suggested_fix?: string | null;
    coverage_reason?: string | null;
    related_task_ids?: string[];
    related_task_titles?: string[];
    trace?: Record<string, unknown>;
    text: string;
  }[];
  categories: Record<string, number>;
  field_mapping: Record<string, string>;
  coverage: {
    passed: boolean;
    covered_count: number;
    total_count: number;
    covered_stage_ids: string[];
    uncovered_stages: { id: string; phase_number: number; name: string }[];
  };
  requirement_trace?: Array<{
    requirement: string;
    source_requirement: string;
    source_clause: string;
    covered: boolean;
    related_tasks: Array<{
      draft_id: string;
      title: string;
      acceptance_criteria: string[];
      covered: boolean;
      [key: string]: unknown;
    }>;
    missing_field?: string | null;
    current_value?: unknown;
    suggested_fix?: string;
    coverage_reason?: string;
  }>;
  uncovered_requirements?: Array<Record<string, unknown>>;
};

export type OutlineSaveResponse = {
  session_id: string;
  suggested_blueprint_id: string;
  confirmed_blueprint_id: string | null;
  task_plan_status: string;
  task_plan_revision: number;
  quality_gate: {
    passed: boolean;
    status_text: string;
    issues: string[];
    field_mapping: Record<string, string>;
  };
};

export type V2ModelChoice = {
  id: string;
  provider: string;
  model: string;
  display_name?: string;
  configured: boolean;
  is_default?: boolean;
  capabilities: string[];
  execution_adapter?: string | null;
  execution_adapter_status?: string | null;
  real_worker_execution?: string | null;
  execution_note?: string;
};

function scrub<T>(value: T): T {
  if (typeof value === 'string') return value.replaceAll('项目大纲', '项目大纲') as T;
  if (Array.isArray(value)) return value.map((item) => scrub(item)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) out[key] = scrub(item);
    return out as T;
  }
  return value;
}

export const v2Api = {
  models: (): Promise<ModelOptionOut[]> => request('/api/v1/plan_ingestion/models/options'),
  modelCapabilities: (): Promise<V2ModelChoice[]> => request('/api/v1/draft_plans/model-capabilities'),
  modelAssignment: (): Promise<Record<string, unknown>> => request('/api/v1/model-assignment'),
  createDraft: (
    raw_requirement: string,
    planning_model_id?: string | null,
    clarify_model_id?: string | null,
    mock_mode = false,
  ): Promise<PlanIngestionSessionOut> =>
    request('/api/v1/plan_ingestion/sessions', {
      method: 'POST',
      body: JSON.stringify({ raw_requirement, planning_model_id, clarify_model_id, mock_mode }),
    }),
  getSession: async (id: string): Promise<PlanIngestionSessionOut> =>
    scrub(await request(`/api/v1/plan_ingestion/sessions/${id}`)),
  addAnswer: (id: string, round: number, content: string) =>
    request(`/api/v1/plan_ingestion/sessions/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ round, role: 'user', content }),
    }),
  askOrGenerate: async (id: string, force_generate = false, request_id?: string): Promise<PlanIngestionSessionOut> =>
    scrub(await request(`/api/v1/plan_ingestion/sessions/${id}/clarify/ask`, {
      method: 'POST',
      body: JSON.stringify({ force_generate, request_id }),
    })),
  modelCalls: async (id: string): Promise<ModelCallOut[]> => scrub(await request(`/api/v1/plan_ingestion/sessions/${id}/model-calls`)),
  listDrafts: async (): Promise<DraftSummary[]> => scrub(await request('/api/v1/draft_plans')),
  getDraftPlan: async (id: string): Promise<DraftPlan> => scrub(await request(`/api/v1/draft_plans/${id}`)),
  generateOutline: async (id: string, payload: { planning_model_id: string; change_request?: string }): Promise<DraftPlan> =>
    scrub(await request(`/api/v1/draft_plans/${id}/generate-outline`, { method: 'POST', body: JSON.stringify(payload) })),
  updateDraftModels: (id: string, payload: { clarify_model_id?: string | null; planning_model_id?: string | null }): Promise<Record<string, unknown>> =>
    request(`/api/v1/draft_plans/${id}/models`, { method: 'POST', body: JSON.stringify(payload) }),
  confirmOutline: async (id: string): Promise<DraftPlan> => scrub(await request(`/api/v1/draft_plans/${id}/confirm-outline`, { method: 'POST', body: JSON.stringify({}) })),
  updateExecutionRouteDraft: (id: string, payload: { stages?: Record<string, Record<string, unknown>>; tasks?: Record<string, Record<string, unknown>> }): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/sessions/${id}/execution-plan-draft`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteDraft: (id: string): Promise<void> => request(`/api/v1/plan_ingestion/sessions/${id}`, { method: 'DELETE' }),
  confirmExecutionPlan: (id: string) => request(`/api/v1/draft_plans/${id}/confirm-execution-plan`, { method: 'POST', body: JSON.stringify({ reason: 'V2 execution plan confirmed' }) }),
  // Real start-task via the existing /plan_ingestion/sessions/{id}/start-task endpoint.
  // Returns the session row whose ``status`` is now ``confirmed/completing/completed``,
  // and the response body carries ``created_task_id`` so the dashboard can navigate
  // to the new Task detail page.
  startTask: async (sessionId: string, taskPackageId: string): Promise<Record<string, unknown>> =>
    scrub(await request(`/api/v1/plan_ingestion/sessions/${sessionId}/start-task`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify({ task_package_id: taskPackageId }),
    })),
  listProjects: async (view = 'active'): Promise<Project[]> => scrub(await request(`/api/v1/projects?view=${encodeURIComponent(view)}`)),
  recycleBin: async (): Promise<RecycleBinEntry[]> => scrub(await request('/api/v1/lifecycle/recycle-bin')),
  archiveProject: (projectId: string, reason = 'V2 用户归档'): Promise<unknown> =>
    request(`/api/v1/lifecycle/projects/${projectId}/archive`, { method: 'POST', body: JSON.stringify({ reason }) }),
  unarchiveProject: (projectId: string): Promise<unknown> =>
    request(`/api/v1/lifecycle/projects/${projectId}/unarchive`, { method: 'POST', body: JSON.stringify({}) }),
  deleteProject: (projectId: string, reason = 'V2 用户删除'): Promise<unknown> =>
    request(`/api/v1/lifecycle/projects/${projectId}/delete`, { method: 'POST', body: JSON.stringify({ reason }) }),
  restoreProject: (projectId: string): Promise<unknown> =>
    request(`/api/v1/lifecycle/projects/${projectId}/restore`, { method: 'POST', body: JSON.stringify({}) }),
  dashboard: async (projectId: string): Promise<ProjectDashboard> => scrub(await request(`/api/v1/v2/projects/${projectId}/dashboard`)),
  stage: async (projectId: string, stageId: string): Promise<any> => scrub(await request(`/api/v1/v2/projects/${projectId}/stages/${stageId}`)),
  task: async (taskId: string): Promise<any> => scrub(await request(`/api/v1/v2/tasks/${taskId}`)),
  retryTask: async (taskId: string): Promise<any> => scrub(await request(`/api/v1/tasks/${taskId}/retry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify({}),
  })),
  redispatchTask: async (taskId: string): Promise<any> => scrub(await request(`/api/v1/tasks/${taskId}/redispatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify({}),
  })),
  reviewTask: async (taskId: string, decision: 'approve' | 'reject' | 'require_fix', reason = ''): Promise<any> => scrub(await request(`/api/v1/tasks/${taskId}/review-decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify({ decision, reason, reviewed_by: 'v2_task_page' }),
  })),

  // ── Phase 2 main chain: persistent task-draft API ─────────────────
  saveOutline: (sessionId: string, payload: unknown): Promise<OutlineSaveResponse> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/outline`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
  getBlueprint: async (sessionId: string): Promise<any> =>
    scrub(await request(`/api/v1/plan_ingestion/sessions/${sessionId}/blueprint`)),
  getBlueprintVersion: async (sessionId: string, blueprintId: string): Promise<any> =>
    scrub(await request(`/api/v1/plan_ingestion/sessions/${sessionId}/blueprint/versions/${blueprintId}`)),
  generateBlueprint: (
    sessionId: string,
    payload: { planning_model_id: string; change_request?: string; gate_a_issues?: string[]; raw_requirement?: string; clarification_context?: string[] },
  ): Promise<any> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/blueprint/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
  confirmBlueprint: async (sessionId: string): Promise<any> =>
    scrub(await request(`/api/v1/plan_ingestion/sessions/${sessionId}/blueprint/confirm`, {
      method: 'POST',
    })),
  generateTaskDrafts: (
    sessionId: string,
    payload: { planning_model_id?: string | null; execution_model?: string | null; executor_agent?: string | null; gate_b_issues?: string[]; force_replace_existing?: boolean },
  ): Promise<TaskDraftsResponse> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/task-drafts/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
  autoFixTaskDrafts: (
    sessionId: string,
    payload: { planning_model_id?: string | null; execution_model?: string | null; executor_agent?: string | null },
  ): Promise<TaskDraftsResponse & { generation_context?: Record<string, unknown> }> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/task-drafts/auto-fix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
  listTaskDrafts: (sessionId: string): Promise<TaskDraftsResponse> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/task-drafts`),
patchTaskDraft: (
    sessionId: string,
    draftId: string,
    patch: Record<string, unknown>,
  ): Promise<{
    draft: TaskDraft;
    task_plan_status: TaskDraftsResponse['task_plan_status'];
    task_plan_revision: number;
    warning: string | null;
    gate_b: TaskPlanQualityGate;
    dependency_format_errors: string[];
  }> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/task-drafts/${draftId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(patch),
    }),
  confirmTaskDrafts: (
    sessionId: string,
  ): Promise<{ session_id: string; task_plan_status: TaskDraftsResponse['task_plan_status']; task_plan_revision: number; drafts: TaskDraft[]; quality_gate: TaskPlanQualityGate }> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/task-drafts/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify({}),
    }),
  taskDraftsQualityGate: (
    sessionId: string,
  ): Promise<{ session_id: string; task_plan_status: TaskDraftsResponse['task_plan_status']; task_plan_revision: number; quality_gate: TaskPlanQualityGate }> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/task-drafts/quality-gate`),
  withdrawTaskDraftsConfirm: (sessionId: string): Promise<TaskDraftsResponse> =>
    request(`/api/v1/plan_ingestion/sessions/${sessionId}/task-drafts/withdraw-confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify({}),
    }),

  // ── Phase 2 main chain: acceptance / final-acceptance ─────────────
  acceptTaskPackage: (
    taskPackageId: string,
    payload: { note?: string; actor?: string } = {},
  ): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/task-packages/${taskPackageId}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
  acceptStage: (
    stageId: string,
    payload: { note?: string; actor?: string } = {},
  ): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/stages/${stageId}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
  finalAcceptProject: (
    projectId: string,
    payload: { note?: string; actor?: string } = {},
  ): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/projects/${projectId}/final-accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
  createRepairPlan: (projectId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/projects/${projectId}/repair-plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify({}),
    }),
  confirmRepairPlan: (
    projectId: string,
    payload: { note?: string; actor?: string } = {},
  ): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/projects/${projectId}/repair-plan/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(payload),
    }),
};
